import {createActionCreators, ImmerReducer} from 'immer-reducer'
import {DiagnosticsStateType, GridSessionType, URLOption} from './state'
import {Status} from 'commonTypes'
import {getActiveDatabaseUrls, GetDiagnosticOptions, getDiagnosticResults} from 'api/graphQL/queries/diagnostics.queries'
import {genericRequestError} from 'redux/toast/toast.duck'
import {DiagnosticSortInput, DiagnosticType} from 'api/graphQL/generated/graphql'

class Diagnostics extends ImmerReducer<DiagnosticsStateType> {
  fetchUrlsGO() {
    this.draftState.status = Status.PENDING
    this.draftState.error = null
    this.draftState.urlOptions = [] 
  }

  fetchUrlsOK(options: URLOption[]) {
    this.draftState.status = Status.OK
    this.draftState.error = null
    this.draftState.urlOptions = options
  }

  fetchUrlsERR(error: unknown) {
    this.draftState.status = Status.REJECTED
    this.draftState.urlOptions = []
    this.draftState.error = error
  }

  fetchScriptsGO() {
    this.draftState.status = Status.PENDING
    this.draftState.diagnosticScriptOptions = []
    this.draftState.error = null
  }

  fetchScriptsOK(options) {
    this.draftState.status = Status.OK
    this.draftState.diagnosticScriptOptions = options
    this.draftState.error = null
  }

  fetchScriptsERR(err) {
    this.draftState.status = Status.REJECTED
    this.draftState.diagnosticScriptOptions = []
    this.draftState.error = err
  }

  runDiagnosticGO() {
    this.draftState.status = Status.PENDING
    this.draftState.diagnosticResults = []
    this.draftState.columnsConfig = []
    this.draftState.totalCount = 0
    this.draftState.error = null
  }

  runDiagnosticOK(results, columns, totalCount) {
    this.draftState.status = Status.OK
    this.draftState.diagnosticResults = results
    this.draftState.columnsConfig = columns
    this.draftState.totalCount = totalCount
    this.draftState.error = null
  }

  runDiagnosticERR(err) {
    this.draftState.status = Status.REJECTED
    this.draftState.diagnosticResults = []
    this.draftState.columnsConfig = []
    this.draftState.totalCount = 0
    this.draftState.error = err
  }

  setSelectedUrl(url: string | null) {
    this.draftState.selectedUrl = url
  }

  setSelectedScript(script: DiagnosticType | null) {
    this.draftState.selectedScript = script
  }

  setShouldRunDiagnostic(val: boolean) {
    this.draftState.shouldRunDiagnostic = val
  }

  updatePagination(pagination: GridSessionType['pagination']) {
    this.draftState.gridSession.pagination = pagination
  }

  updatePaginationIndex(value) {
    this.draftState.gridSession.pagination.pageIndex = value
  }

  updateSorting(sorting: GridSessionType['sorting']) {
    this.draftState.gridSession.sorting = sorting
  }

  updateColumnOrder(columnOrder: GridSessionType['columnOrder']) {
    this.draftState.gridSession.columnOrder = columnOrder
  }

  clearResults() {
    this.draftState.diagnosticResults = []
    this.draftState.columnsConfig = []
    this.draftState.totalCount = 0
    this.draftState.status = Status.IDLE
    this.draftState.error = null
  }
  setDiagnosticDetailsItem(item: any) {
    this.draftState.diagnosticDetailsItem = item
    this.draftState.showDetailsPanel = true
  }
  clearDiagnosticDetailsItem() {
    this.draftState.diagnosticDetailsItem = null
    this.draftState.showDetailsPanel = false
  }
  setSearchQuery(search:string) {
    this.draftState.gridSession.filters.searchQuery = search
  }
}

export const fetchDiagnosticsUrls = () => async function (dispatch) {
  dispatch(diagnosticsAC.fetchUrlsGO())
  try {
    const {data} = await getActiveDatabaseUrls()
    const databases = data?.databases ?? []

    const options: URLOption[] = databases.map((db) => ({
      label: db.hostname ?? `Database ${db.databaseId}`,
      value: db.databaseId,
      subLabel: db.databaseId,
      databaseId: db.databaseId,
      hostname: db.hostname,
    }))

    dispatch(diagnosticsAC.fetchUrlsOK(options))
  } catch (err: any) {
    dispatch(diagnosticsAC.fetchUrlsERR(err))
    dispatch(genericRequestError())
  }
}

export const fetchDiagnosticsScripts = () => async function (dispatch) {
  dispatch(diagnosticsAC.fetchScriptsGO())
  try {
    const {data} = await GetDiagnosticOptions()
    const scripts = data?.diagnosticOptions ?? []

    const options = scripts.map((s: { name: string; diagnosticScript: DiagnosticType }) => ({
      label: s.name,
      value: s.diagnosticScript,
    }))

    dispatch(diagnosticsAC.fetchScriptsOK(options))
  } catch (err: any) {
    dispatch(diagnosticsAC.fetchScriptsERR(err))
    dispatch(genericRequestError())
  }
}

export const RunDiagnostic = (databaseId: string, script: DiagnosticType, skip: number, take: number, searchText?: string, sorting?: DiagnosticSortInput) => async function (dispatch) {
  dispatch(diagnosticsAC.runDiagnosticGO())

  try {
    const {data} = await getDiagnosticResults(databaseId, script, skip, take, searchText, sorting)
    const results = data?.diagnostics?.items ?? []
    const rawColumns = data?.diagnostics?.columns ?? []
    const columns = rawColumns.map((key) => ({
      accessorKey: key,
      header: key.replace(/([A-Z])/g, ' $1').trim(),
    }))
    const totalCount = data?.diagnostics?.totalCount ?? 0

    dispatch(diagnosticsAC.runDiagnosticOK(results, columns, totalCount))
  } catch (err: any) {
    dispatch(diagnosticsAC.runDiagnosticERR(err))
    dispatch(genericRequestError())
  }
}

export const diagnosticsAC = createActionCreators(Diagnostics)

export default Diagnostics
